import { Client } from 'urql'
import { Application_Type, Mutation_Root, Query_Root } from 'src/@types/graphql'
import { getApplicationTypesByProcesses } from 'src/screens/administration/application-type-management/applicationTypeManagementQueries'
import { Option } from 'src/shared/form/control'
import { PROCESS_TYPE } from 'src/shared/constants/constants'
import { deleteCriteriaConfigById } from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'

export const fetchApplicationTypesByProcesses = (urqlClient: Client) => async (processes: PROCESS_TYPE[]) => {
  const { data, error } = await urqlClient
    .query<
      {
        application_type: Query_Root['application_type']
      },
      {
        processes: string[]
      }
    >(getApplicationTypesByProcesses, {
      processes,
    })
    .toPromise()

  if (error || !data?.application_type) {
    throw new Error('Resource not found')
  }

  return {
    fetchedApplicationTypes: data.application_type,
  }
}

export const deleteEvaluationCriteria = (urqlClient: Client) => async (evaluationCriteriaId: number) => {
  const { data, error } = await urqlClient
    .mutation<{ delete_criteria_config: Mutation_Root['delete_criteria_config'] }, { criteriaId: number }>(
      deleteCriteriaConfigById,
      { criteriaId: evaluationCriteriaId },
    )
    .toPromise()

  if (error || data?.delete_criteria_config?.affected_rows !== 1) {
    throw new Error('Evaluation criteria deletion error')
  }

  return true
}

export const mapApplicationTypesToOptions = (applicationTypes: Application_Type[]): Option[] =>
  applicationTypes.map((aType) => ({
    label: aType.key,
    value: aType.id,
  }))

export const shouldResetApplicationTypeFilterAfterProcessFilterChange = (
  processFilterOption: Option | null,
  applicationTypeFilterOption: Option | null,
  allApplicationTypes: Application_Type[],
): boolean => {
  if (processFilterOption?.value == null) {
    // clear applicationTypeFilter when processFilter is being cleared
    return true
  }

  if (applicationTypeFilterOption?.value == null) {
    // don't clear applicationTypeFilter if it is already set to ALL (cleared)
    return false
  }

  // check whether applicationTypeFilter should be cleared based on newly selected processFilter
  const processFilterValue = processFilterOption.value as string
  const applicationTypeFilterValue = applicationTypeFilterOption.value as number

  const isApplicationTypeOptionCompatibleWithProcessOption = allApplicationTypes
    .filter((aType) => aType.process === processFilterValue)
    .some((aType) => aType.id === applicationTypeFilterValue)

  // clear application type filter if it is not compatible with process filter
  return !isApplicationTypeOptionCompatibleWithProcessOption
}

export const getProcessFilterOptionForApplicationType = (
  selectedApplicationType: Application_Type,
  criteriaProcessOptions: Option[],
): Option => {
  const newProcessFilterValue = criteriaProcessOptions.find(
    (option) => option.value === selectedApplicationType.process,
  )

  return newProcessFilterValue ?? (criteriaProcessOptions.find((option) => option.value == null) as Option)
}
